import React from 'react';
import PropTypes from 'prop-types';

import { FormProvider } from 'react-hook-form';

import { t } from 'i18next';

import {
  styled,
  Box,
} from '@mui/material';

import {
  Label,
  Button,
  KeyIcon,
  Typography,
  FormPasswordField,
  FormCheckboxField,
} from '@datanomik/commons-ui';

import { UserShared2Line } from '@datanomik/commons-ui/icons';

import Grid from '../../common/Grid';
import Sticky from '../../common/Sticky';
import PasswordStrength from '../../common/PasswordStrength';

import ContentBox from '../LogIn/ContentBox';
import { TermsAndConditions } from '../TermsAndConditions';

import useActivateNewUser from './hooks';
import styles from './styles';

const ActivateNewUserRaw = ({ className }) => {
  const {
    formMethods,
    passwordPolicies,
    handleOnSubmit,

    openLegal,
    legalHtml,
    acceptLegalTemplate,
    handleCloseLegalTemplate,
  } = useActivateNewUser();

  const {
    MIN_LOWERCASE_CHARACTERS,
    MIN_UPPERCASE_CHARACTERS,
    MIN_NUMERIC_CHARACTERS,
    MIN_SPECIAL_CHARACTERS,
    MIN_LENGTH,
  } = passwordPolicies;

  return (
    <Box className={`${className}`}>
      <Box className="dn-container">
        <Grid container>
          <Grid item xs={12} md="auto">
            <Sticky position="top" rootSelector="body">
              <ContentBox />
            </Sticky>
          </Grid>
          <Grid item xs={12} md className="dn-flex-center">
            <Box maxWidth="350px" m="auto 0">
              <Grid container>
                <Grid item xs={12} textAlign="center">
                  <KeyIcon size="xlarge" color="neutral">
                    <UserShared2Line />
                  </KeyIcon>
                </Grid>
                <Grid item xs={12} textAlign="center" mb="14px">
                  <Typography variant="h5">
                    {t('User Activation')}
                  </Typography>
                  <Typography variant="p3" color="text.500" mt="4px">
                    {t('Enter your details to active.')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box maxWidth="350px" m="0 auto">
                    <FormProvider {...formMethods}>
                      <form onSubmit={formMethods.handleSubmit(handleOnSubmit)} noValidate>
                        <Grid container>
                          <Grid item xs={12}>
                            <FormPasswordField
                              name="password"
                              control={formMethods.control}
                              label={<Label text={t('Password')} />}
                              labelText={t('Password')}
                              required
                              validationRules={{
                                validate: {
                                  lowerChar: (v) => new RegExp(`^(?=.*[a-z]{${MIN_LOWERCASE_CHARACTERS},})`).test(v) || t('The password must contain at least {{count}} lowercase alphabetical characters.', { count: MIN_LOWERCASE_CHARACTERS }),
                                  upperChar: (v) => new RegExp(`^(?=.*[A-Z]{${MIN_UPPERCASE_CHARACTERS},})`).test(v) || t('The password must contain at least {{count}} uppercase alphabetical characters.', { count: MIN_UPPERCASE_CHARACTERS }),
                                  numberChar: (v) => new RegExp(`^(?=.*[0-9]{${MIN_NUMERIC_CHARACTERS},})`).test(v) || t('The password must contain at least {{count}} numeric characters.', { count: MIN_NUMERIC_CHARACTERS }),
                                  strangeChar: (v) => new RegExp(`^(?=.*[^a-zA-Z0-9]{${MIN_SPECIAL_CHARACTERS},})`).test(v) || t('The password must contain at least {{count}} special characters.', { count: MIN_SPECIAL_CHARACTERS }),
                                  lengthChar: (v) => new RegExp(`^(?=.{${MIN_LENGTH},})`).test(v) || t('The password must be {{count}} characters or longer.', { count: MIN_LENGTH }),
                                },
                              }}
                              localeText={{
                                isRequired: t('IS REQUIRED'),
                              }}
                              slotProps={{
                                htmlInput: {
                                  autoComplete: 'off',
                                },
                              }}
                            />
                            <Box p="4px">
                              <PasswordStrength password={formMethods.watch('password')} />
                            </Box>
                          </Grid>
                          <Grid item xs={12}>
                            <FormCheckboxField
                              className="hide-asterisk"
                              name="acceptedDataProcessing"
                              control={formMethods.control}
                              required
                              labelText={t('Accept the use')}
                              localeText={{
                                isRequired: t('IS REQUIRED'),
                              }}
                              label={(
                                <Label
                                  text={(
                                    <Typography variant="p6">
                                      {t('I agree to the use and processing of my data by Datanomik (Uruguay and Brazil) for commercial purposes and for the improvement of its services.')}
                                    </Typography>
                                    )}
                                />
                                )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button type="submit" fullWidth>
                              {t('Activate')}
                            </Button>
                          </Grid>
                        </Grid>
                      </form>
                    </FormProvider>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <TermsAndConditions
        openLegal={openLegal}
        legalContent={legalHtml}
        onAccept={acceptLegalTemplate}
        onClose={handleCloseLegalTemplate}
      />
    </Box>
  );
};

ActivateNewUserRaw.propTypes = {
  className: PropTypes.string,
};

export const ActivateNewUser = styled(ActivateNewUserRaw)(styles);

ActivateNewUser.propTypes = ActivateNewUserRaw.propTypes;
